import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { openWebCenterDecisionCall } from '@/lib/analytics';
import {
  ArrowRight,
  Calendar,
  Compass,
  Scale,
  Target,
  Puzzle,
  Mail,
  ShieldCheck,
  Building2,
  Workflow,
  HelpCircle,
  Layers3,
  FileSearch,
} from 'lucide-react';
import Navigation from '@/components/Navigation';
import SEO from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import WebCenterEngagementHistory from '@/components/WebCenterEngagementHistory';
import LastUpdated from '@/components/LastUpdated';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { FOUNDER, founderPersonLd } from '@/lib/founder';

const ALTERNATIVE_CATEGORIES = [
  {
    icon: Layers3,
    title: 'Open-source ECM platforms',
    detail:
      'Open-source content management platforms with customizable repositories, metadata models, and workflow engines. Strong for teams that want full control of the platform and have engineering capacity to extend it. AP automation is not the focus — it is one of many possible workloads.',
  },
  {
    icon: Building2,
    title: 'Large enterprise ECM platforms',
    detail:
      'Established enterprise content management platforms aimed at broad records, compliance, and unstructured-content workloads across the enterprise. Strong for organizations standardizing on a single content platform across HR, legal, contracts, and records. AP is one module among many.',
  },
  {
    icon: Workflow,
    title: 'Cloud-native ECM platforms',
    detail:
      'Newer cloud-native content platforms with API-first repositories, modern UX, and SaaS delivery. Strong on developer experience and modern architecture. AP automation typically requires bolt-on workflow and extraction services rather than coming pre-integrated with Oracle EBS or Fusion.',
  },
  {
    icon: FileSearch,
    title: 'Document-management-as-a-service',
    detail:
      'Lightweight DMS offerings focused on storage, search, and basic workflow over scanned and digital documents. Strong fit for teams replacing shared drives or legacy imaging. Generally do not include AP-grade coding workflow, AME routing, or supplier-master-aware exception handling.',
  },
];

const WHAT_EZCLOUD_IS = [
  {
    icon: Target,
    title: 'Purpose-built for Oracle AP',
    detail:
      'EZ Cloud is an AP automation platform, not a general-purpose ECM. The data model, workflow engine, AI extraction, exception queues, and reporting are designed around invoice processing on Oracle EBS and Oracle Fusion specifically.',
  },
  {
    icon: ShieldCheck,
    title: 'Preserves Oracle ERP investment',
    detail:
      'Oracle EBS or Fusion remains the system of record. Supplier master, COA, AME routing, period close, and audit trail stay where they are. EZ Cloud modernizes the AP automation layer in front of the ERP — capture, extraction, coding, approvals — without disturbing the Oracle stack underneath.',
  },
  {
    icon: Mail,
    title: 'AI Email Exception Prevention',
    detail:
      'Supplier email triage — wrong PO, wrong remit-to, missing attachment, duplicate submission — is handled before invoices enter the workflow. This is a category of AP work most ECM platforms do not address at all because they treat invoices as generic documents.',
  },
  {
    icon: Compass,
    title: 'Built by Oracle WebCenter specialists',
    detail:
      'Founder-led by a 25-year Oracle AP specialist with 20+ years in WebCenter Content, Imaging, Forms Recognition, SOA, WebLogic, and ADF. The platform reflects how Oracle AP actually runs, not a generic invoice workflow with an Oracle adapter bolted on.',
  },
];

const WHERE_ECM_FITS_BETTER = [
  {
    title: 'HR records and personnel files',
    detail:
      'Employee records, hiring documentation, performance reviews, and confidential personnel files belong in an HR-aware records system or a general ECM with HR retention policies. EZ Cloud does not target HR records.',
  },
  {
    title: 'Contract lifecycle management',
    detail:
      'Contract drafting, redlining, negotiation workflows, and contract repositories are best served by purpose-built CLM systems or general ECM platforms with CLM modules. EZ Cloud is not a contract management platform.',
  },
  {
    title: 'Marketing assets and DAM',
    detail:
      'Brand assets, images, video, and creative files are best served by digital asset management platforms or general ECM with DAM modules. EZ Cloud has no DAM features.',
  },
  {
    title: 'Broad enterprise records retention',
    detail:
      'Long-tail records management across HR, legal, regulatory filings, and operational records — with retention schedules and legal-hold workflows across the enterprise — is the natural home of a general-purpose ECM platform. EZ Cloud focuses on AP.',
  },
  {
    title: 'Engineering drawings and CAD repositories',
    detail:
      'Versioned engineering, manufacturing, or construction document repositories are best served by domain-specific document control systems. Not an EZ Cloud use case.',
  },
  {
    title: 'Knowledge management and intranet content',
    detail:
      'Internal knowledge bases, policy libraries, and intranet content publishing fit a portal or knowledge platform. EZ Cloud is not designed for this.',
  },
];

const WHERE_EZCLOUD_FITS_BETTER = [
  {
    title: 'Oracle EBS or Fusion is the system of record',
    detail:
      'Customers running Oracle E-Business Suite Payables or Oracle Fusion Cloud ERP who want to preserve the ERP investment and modernize the AP automation layer specifically. AME, supplier master, multi-fund coding, and period close stay on Oracle.',
  },
  {
    title: 'AP is the actual problem being solved',
    detail:
      'When the real conversation is about invoice volume, exception rates, supplier email burden, coding accuracy, and approval cycle time — not enterprise-wide content management — an AP-specific platform engages the problem directly.',
  },
  {
    title: 'WebCenter AP automation needs to be modernized',
    detail:
      'For customers on WebCenter Imaging, WebCenter Forms Recognition, WebCenter Enterprise Capture, or AP Image Solution who want to move off the 12c stack while preserving the AP workflow logic, supplier master integration, and audit trail that already exist.',
  },
  {
    title: 'Audit posture must be preserved',
    detail:
      'AP-on-Oracle customers in regulated industries — healthcare, public sector, financial services — where the audit trail tying invoices to GL postings, AME approvers, and supporting documentation cannot be broken during a platform change.',
  },
];

const COMPARISON_ROWS = [
  {
    dimension: 'Primary use case',
    ezcloud: 'AP automation on Oracle EBS / Fusion',
    ecm: 'General enterprise content management',
  },
  {
    dimension: 'Oracle ERP integration depth',
    ezcloud:
      'Native to EBS (open interface, supplier master, AME, period close) and Fusion (OIC, REST APIs, ERP-native posting)',
    ecm: 'Generally requires custom integration, middleware, or third-party connectors',
  },
  {
    dimension: 'Audit trail preservation during migration',
    ezcloud:
      'Existing invoice-to-GL audit trail preserved; new automation layer added without breaking ERP-side audit',
    ecm: 'Audit model is the ECM platform\'s own — AP audit trail typically reconstructed',
  },
  {
    dimension: 'Supplier email exception prevention',
    ezcloud:
      'Built-in AI Email Exception Prevention — wrong PO, wrong remit-to, missing attachment caught upstream of the workflow',
    ecm: 'Typically out of scope — invoices treated as generic documents on arrival',
  },
  {
    dimension: 'AI extraction with Oracle context',
    ezcloud:
      'Extraction tuned to Oracle supplier master, COA, multi-fund coding, and AP-specific exception types',
    ecm: 'General-purpose extraction; Oracle-specific context requires custom configuration',
  },
  {
    dimension: 'Non-AP content (HR, contracts, DAM)',
    ezcloud: 'Out of scope — AP only',
    ecm: 'Broad coverage across enterprise content workloads',
  },
  {
    dimension: 'Cost model',
    ezcloud:
      'SaaS for the AP automation layer; no separate ECM license; ERP licenses unchanged',
    ecm: 'ECM platform license + AP module (if any) + integration build + ongoing infra',
  },
];

const SCENARIOS = [
  {
    icon: Compass,
    title: 'Started evaluating ECM platforms — realized AP is the actual problem',
    detail:
      'The replacement conversation started as "what replaces WebCenter Content" but the operational pain is invoice volume, supplier email exceptions, and AP coding accuracy. The right scope is the AP automation layer, not a full ECM replacement project.',
  },
  {
    icon: Layers3,
    title: 'Want to consolidate AP + adjacent document management',
    detail:
      'AP needs modernizing, and there is also a body of non-AP content — HR records, contracts, scanned legal files — that needs a home. The architectural question is whether to put both on one platform or two. An AP-specific platform plus a general ECM is often a cleaner long-term answer than forcing both into one system.',
  },
  {
    icon: Scale,
    title: 'RFP-driven evaluation requiring vendor neutrality',
    detail:
      'Procurement requires multiple vendors on the shortlist for a content management or AP automation RFP. EZ Cloud is the AP-specific entry on the shortlist alongside one or two general ECM platforms — and the comparison happens dimension by dimension, not category by category.',
  },
  {
    icon: Building2,
    title: 'Already chose an ECM platform — where does AP fit',
    detail:
      'An enterprise standard ECM is already in place or already selected for HR, legal, and records. The question is whether AP automation should run on the same platform or sit alongside it on a purpose-built AP layer. Both architectures are legitimate — the decision is driven by AP-team requirements, not by the ECM standard.',
  },
];

const ENGAGEMENT_TIERS = [
  {
    tag: 'Tier 1 · Alternatives Decision Call',
    title: 'Free 30-minute call to scope the comparison',
    body: 'Walk through the current WebCenter Content footprint, what is driving the replacement evaluation, and which alternatives are on the shortlist. Output: an honest read on whether EZ Cloud is the right fit for your AP layer, and what other categories of alternative match the non-AP content workloads.',
  },
  {
    tag: 'Tier 2 · Fit-and-Gap Assessment',
    title: 'Fixed-fee read of your AP-on-WebCenter implementation',
    body: 'Founder-led review of your existing WebCenter Imaging, Forms Recognition, Capture, and ERP integration footprint. Output: an AP-specific fit-and-gap document mapping current functionality to EZ Cloud and surfacing non-AP content workloads that would need a different home.',
  },
  {
    tag: 'Tier 3 · Strategic Advisory Retainer',
    title: 'Founder-level guidance through the replacement evaluation',
    body: 'Monthly retainer with Andrew for customers running a multi-vendor evaluation across AP and broader ECM. Strategic guidance on shortlist composition, RFP scoring, architecture decisions (one platform vs two), and Oracle-side integration considerations.',
  },
  {
    tag: 'Tier 4 · Forward Deployed Engineering',
    title: 'Embedded delivery for the AP automation layer',
    body: 'When the decision lands on EZ Cloud for the AP layer, an FDE engagement embeds a senior Oracle-AP engineer alongside your team for the implementation. Capture, extraction, AME routing, supplier master alignment, and ERP integration delivered by the same people who designed the platform.',
  },
];

const FAQS = [
  {
    q: 'Is EZ Cloud an ECM platform?',
    a: 'No — EZ Cloud is an AP automation platform purpose-built for Oracle EBS and Oracle Fusion customers, not a general enterprise content management platform. It handles invoice capture, AI extraction, coding, approval workflow, exception management, and ERP posting. For HR records, contract lifecycle, marketing assets, or broad records retention, a general ECM is the right category of tool.',
  },
  {
    q: 'Can EZ Cloud replace WebCenter Content entirely?',
    a: 'EZ Cloud replaces the AP-automation workloads on WebCenter Content, Imaging, Forms Recognition, and Capture, but does not replace non-AP WebCenter Content workloads like HR records, contracts, or broad records management. The AP-automation scope it covers includes invoice capture, extraction, coding, approval routing, supplier email handling, and audit trail through to GL posting. Customers running WebCenter Content as a general ECM should evaluate a general ECM replacement for those non-AP workloads.',
  },
  {
    q: 'What about non-AP content stored in WebCenter Content?',
    a: 'Non-AP content in WebCenter Content — HR documents, contracts, project files, scanned correspondence — is best handled by a general ECM platform, a domain-specific records system, or in some cases the customer\'s existing SharePoint or Google Workspace footprint. Most WebCenter Content implementations carry a mix of AP content and non-AP content. EZ Cloud focuses on the AP slice, and the Fit-and-Gap Assessment surfaces this split explicitly.',
  },
  {
    q: 'What hybrid architecture options exist?',
    a: 'The common hybrid architecture for Oracle customers is EZ Cloud for the AP automation layer alongside a separate ECM platform for general content. AP invoices, supporting documents, and the AP audit trail live on EZ Cloud and Oracle. HR records, contracts, and broader records live on the chosen ECM. The two systems stay independent — AP does not need to share a platform with HR records to function well, and most large organizations end up with multiple content systems anyway.',
  },
  {
    q: 'How is the audit trail preserved when migrating from WebCenter Content?',
    a: 'The Oracle-side audit trail stays on Oracle EBS or Fusion through the migration — invoice header and lines in AP, AME approver chain, GL distribution, period close, and supplier master changes are unchanged. EZ Cloud adds its own automation-layer audit (capture timestamps, extraction confidence, approval routing, exception resolution) tied to the same Oracle invoice IDs. Audit teams can trace any invoice from the supplier email or scanned document through extraction, approval, posting, and payment without a gap.',
  },
  {
    q: 'How does Oracle ERP integration depth compare to a general ECM?',
    a: 'EZ Cloud is built directly around Oracle EBS Payables open interface and Oracle Fusion REST APIs and OIC, with supplier master sync, AME routing, multi-fund coding rules, descriptive flexfields, and period close as first-class concerns. General ECM platforms typically integrate with Oracle via a generic connector or middleware, and AP-specific Oracle behaviors — AME, holds, period close, supplier alternates — require custom development. The difference shows up most clearly during month-end close and during AP-specific exception types.',
  },
  {
    q: 'How does the cost model compare?',
    a: 'EZ Cloud is SaaS for the AP automation layer with no separate ECM license required and no change to Oracle ERP licensing. A general ECM replacement typically carries an ECM platform license, an AP module license (if AP is in scope), integration build cost, and ongoing infrastructure cost. The cost comparison depends heavily on whether non-AP content workloads are in the same replacement scope — if AP is the only workload being replaced, an AP-specific platform is usually the more efficient cost model.',
  },
  {
    q: 'What happens on an Alternatives Decision Call?',
    a: 'An Alternatives Decision Call is a 30-minute working session with Andrew Blackman, founder of EZ Cloud and a 25-year Oracle AP specialist. We walk through the current WebCenter Content footprint, what is driving the replacement evaluation, what other alternatives are on the shortlist, and whether EZ Cloud is the right fit for the AP layer specifically. The honest answer is sometimes "yes, here is how", and sometimes "your AP scope is small enough that a general ECM AP module is fine" — either way, you leave with a clearer decision frame.',
  },
];

const WebCenterContentAlternatives = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  const personSchema = { '@context': 'https://schema.org', ...founderPersonLd };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'TechArticle',
    headline:
      'Oracle WebCenter Content Alternatives — The Replacement-Shopping Comparison | EZ Cloud',
    description:
      'Customers considering replacement of Oracle WebCenter Content typically evaluate multiple alternatives. EZ Cloud is the alternative built for Oracle AP customers specifically — preserve Oracle EBS / Fusion integration, preserve audit posture, modernize the AP automation layer. How EZ Cloud compares to generic content management replacements.',
    author: { '@id': 'https://ezcloud.ai/about#andrew-blackman' },
    publisher: { '@id': 'https://ezcloud.ai/#organization' },
    datePublished: '2026-06-29',
    dateModified: '2026-06-29',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': 'https://ezcloud.ai/solutions/webcenter-content-alternatives',
    },
    about: [
      { '@type': 'Thing', name: 'Oracle WebCenter Content Alternatives' },
      { '@type': 'Thing', name: 'WebCenter Content Replacement' },
      { '@type': 'Thing', name: 'Enterprise Content Management' },
      { '@type': 'Thing', name: 'Accounts Payable Automation' },
      { '@type': 'Thing', name: 'Oracle ERP Integration' },
    ],
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Oracle WebCenter Content Alternatives — The Replacement-Shopping Comparison | EZ Cloud"
        description="Customers considering replacement of Oracle WebCenter Content typically evaluate multiple alternatives. EZ Cloud is the alternative built for Oracle AP customers specifically — preserve Oracle EBS / Fusion integration, preserve audit posture, modernize the AP automation layer. How EZ Cloud compares to generic content management replacements."
        canonical="https://ezcloud.ai/solutions/webcenter-content-alternatives"
        breadcrumbs={[
          { name: 'Home', url: 'https://ezcloud.ai/' },
          { name: 'Solutions', url: 'https://ezcloud.ai/solutions/modernize-oracle-webcenter' },
          {
            name: 'WebCenter Content Alternatives',
            url: 'https://ezcloud.ai/solutions/webcenter-content-alternatives',
          },
        ]}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(personSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <Navigation />
      <Breadcrumbs />
      <LastUpdated date="2026-06-29" />

      <main>
        {/* Hero */}
        <section className="bg-hero-gradient text-white pt-16 pb-20 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-br from-primary/20 to-transparent" />
            <div className="absolute top-20 right-20 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
          </div>
          <div className="container mx-auto px-4 max-w-5xl relative">
            <div className="flex items-center gap-2 text-accent text-sm font-semibold uppercase tracking-wide mb-4">
              <Scale className="w-4 h-4" aria-hidden />
              Replacement shopping · WebCenter Content alternatives
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Oracle WebCenter Content alternatives
              <span className="block text-accent mt-3 text-2xl md:text-3xl lg:text-4xl font-bold">
                The honest comparison for replacement shoppers.
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-white/80 max-w-3xl mb-8">
              For customers considering replacement of WebCenter Content. The honest comparison:
              EZ Cloud is built for Oracle AP customers specifically — not a generic content
              management replacement.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                size="lg"
                variant="accent"
                className="font-semibold"
                onClick={() => openWebCenterDecisionCall('wcc-alternatives-hero')}
              >
                <Calendar className="mr-2 w-5 h-5" aria-hidden />
                Book an Alternatives Decision Call
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
              <Button size="lg" variant="outline-on-dark" asChild>
                <Link to="/solutions/modernize-oracle-webcenter">
                  See the full modernization path
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* TL;DR */}
        <section className="py-14 bg-white border-b border-gray-100">
          <div className="container mx-auto px-4 max-w-4xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
              What this is
            </p>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-5 leading-tight">
              An honest comparison for replacement shoppers
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                <strong className="text-gray-900">
                  Buyers searching "Oracle WebCenter Content alternatives" are usually evaluating
                  generic content management replacements
                </strong>{' '}
                — open-source ECM, cloud-native ECM, large enterprise ECM platforms, or
                document-management-as-a-service. All of those are legitimate categories. None of
                them are purpose-built for Oracle AP modernization.
              </p>
              <p>
                <strong className="text-gray-900">EZ Cloud is the alternative built for Oracle
                AP customers specifically.</strong>{' '}
                It preserves Oracle EBS or Fusion as the system of record, preserves the audit
                trail through to GL posting, and modernizes the AP automation layer — capture,
                extraction, coding, approval workflow, supplier email exception prevention. It is
                not a general-purpose ECM replacement.
              </p>
              <p>
                This page is the comparison: where generic ECM platforms fit better than EZ Cloud,
                where EZ Cloud fits better than generic ECM, and the dimensions that actually
                separate them. For the full set of forward paths from the December 2026 12c
                support cliff, see the{' '}
                <Link
                  to="/solutions/modernize-oracle-webcenter"
                  className="text-primary font-semibold hover:text-accent transition-colors"
                >
                  modernization hub
                </Link>{' '}
                and the{' '}
                <Link
                  to="/solutions/webcenter-12c-end-of-support-decision"
                  className="text-primary font-semibold hover:text-accent transition-colors"
                >
                  12c end-of-support decision frame
                </Link>
                .
              </p>
            </div>
          </div>
        </section>

        {/* What buyers usually find */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              The alternatives landscape
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              What buyers usually find when they search "WebCenter Content alternatives"
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Four broad categories of replacement. Each has a place. None are purpose-built for
              Oracle AP modernization specifically.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {ALTERNATIVE_CATEGORIES.map(({ icon: Icon, title, detail }) => (
                <Card key={title} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-accent" aria-hidden />
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-semibold mb-1">{title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{detail}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* What EZ Cloud actually is */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Where EZ Cloud sits in this comparison
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              What EZ Cloud actually is
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Not a general ECM replacement. A purpose-built AP automation platform for Oracle EBS
              and Oracle Fusion customers, designed to preserve the Oracle investment and
              modernize the AP layer specifically.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {WHAT_EZCLOUD_IS.map(({ icon: Icon, title, detail }) => (
                <Card key={title} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-accent" aria-hidden />
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-semibold mb-1">{title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{detail}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
            <div className="text-center mt-10">
              <Link
                to="/integrations/oracle"
                className="inline-flex items-center font-semibold text-primary hover:text-accent transition-colors"
              >
                See the Oracle EBS + Fusion integration detail
                <ArrowRight className="w-4 h-4 ml-1" aria-hidden />
              </Link>
            </div>
          </div>
        </section>

        {/* Where ECM fits better */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              The honest part
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Where generic ECM platforms fit better than EZ Cloud
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              EZ Cloud is not a general content management replacement. These workloads belong on
              a different category of platform — and we will say so on the Decision Call.
            </p>
            <div className="grid md:grid-cols-2 gap-5">
              {WHERE_ECM_FITS_BETTER.map((row) => (
                <Card key={row.title} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <h3 className="text-gray-900 font-semibold mb-2">{row.title}</h3>
                  <p className="text-sm text-gray-700 leading-relaxed">{row.detail}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Where EZ Cloud fits better */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              The fit
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Where EZ Cloud fits better than generic ECM
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Oracle AP customers who want to preserve the Oracle ERP investment and modernize the
              AP layer specifically — without taking on a full enterprise content management
              replacement project.
            </p>
            <div className="grid md:grid-cols-2 gap-5">
              {WHERE_EZCLOUD_FITS_BETTER.map((row) => (
                <Card key={row.title} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <h3 className="text-gray-900 font-semibold mb-2">{row.title}</h3>
                  <p className="text-sm text-gray-700 leading-relaxed">{row.detail}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Honest comparison table */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-6xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Dimension by dimension
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              The honest comparison table
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Seven dimensions that actually separate an AP-specific platform from a generic ECM
              replacement. Run the same dimensions against any alternative on your shortlist.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full border border-gray-200 rounded-xl overflow-hidden bg-white">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left text-xs font-bold uppercase tracking-wide text-accent px-5 py-4 w-1/5">
                      Dimension
                    </th>
                    <th className="text-left text-xs font-bold uppercase tracking-wide text-accent px-5 py-4 w-2/5">
                      EZ Cloud (AP-specific)
                    </th>
                    <th className="text-left text-xs font-bold uppercase tracking-wide text-accent px-5 py-4 w-2/5">
                      Generic ECM replacement
                    </th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {COMPARISON_ROWS.map((row) => (
                    <tr key={row.dimension} className="border-b border-gray-200 last:border-b-0">
                      <td className="px-5 py-4 font-semibold text-gray-900 bg-gray-50">
                        {row.dimension}
                      </td>
                      <td className="px-5 py-4 text-gray-700">{row.ezcloud}</td>
                      <td className="px-5 py-4 text-gray-700">{row.ecm}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="text-center mt-8">
              <Link
                to="/solutions/webcenter-oci-marketplace-vs-modernize"
                className="inline-flex items-center font-semibold text-primary hover:text-accent transition-colors"
              >
                See the OCI Marketplace vs modernize comparison
                <ArrowRight className="w-4 h-4 ml-1" aria-hidden />
              </Link>
            </div>
          </div>
        </section>

        {/* Common scenarios */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              What we hear
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Common scenarios from replacement shoppers
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Four shapes of conversation we hear from customers who searched for WebCenter
              Content alternatives. Each one points at a different right answer.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {SCENARIOS.map(({ icon: Icon, title, detail }) => (
                <Card key={title} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-accent" aria-hidden />
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-semibold mb-2">{title}</h3>
                      <p className="text-sm text-gray-700 leading-relaxed">{detail}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Engagement tiers */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              How EZ Cloud engages on replacement evaluations
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Four engagement tiers for alternatives shoppers
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              From a free Decision Call through Forward Deployed Engineering delivery. Standard
              consulting is the default; FDE is the embedded-delivery mode when implementation
              lands on EZ Cloud.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {ENGAGEMENT_TIERS.map((t) => (
                <Card key={t.tag} className="p-7 bg-white border border-gray-200 shadow-sm">
                  <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
                    {t.tag}
                  </p>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{t.title}</h3>
                  <p className="text-gray-700 leading-relaxed">{t.body}</p>
                </Card>
              ))}
            </div>
            <p className="text-center text-sm text-gray-500 mt-8 max-w-2xl mx-auto">
              Engagement pricing is established per situation on a Decision Call.
            </p>
          </div>
        </section>

        {/* Cross-link to decision guide */}
        <section className="py-12 bg-accent/5 border-y border-accent/30">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
              Free resource
            </p>
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              The Oracle WebCenter AP December 2026 Decision Guide
            </h3>
            <p className="text-gray-700 leading-relaxed mb-6 max-w-2xl mx-auto">
              The structured guide to the forward paths from the 12c support cliff —
              including a vendor-evaluation checklist that runs the same dimensions you see on
              this page across whichever alternatives are on your shortlist.
            </p>
            <Link
              to="/resources/webcenter-decision-guide"
              className="inline-flex items-center font-semibold text-white bg-accent hover:bg-accent/90 transition-colors px-6 py-3 rounded-lg"
            >
              Read the Decision Guide
              <ArrowRight className="w-4 h-4 ml-2" aria-hidden />
            </Link>
          </div>
        </section>

        <WebCenterEngagementHistory />

        {/* FAQ */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Common questions
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-10">
              Direct answers on WebCenter Content alternatives
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((f, i) => (
                <AccordionItem key={f.q} value={`item-${i}`}>
                  <AccordionTrigger className="text-left text-gray-900">
                    <span className="flex items-start gap-3">
                      <HelpCircle
                        className="w-5 h-5 text-accent flex-shrink-0 mt-0.5"
                        aria-hidden
                      />
                      <span>{f.q}</span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-hero-gradient text-white">
          <div className="container mx-auto px-4 max-w-3xl text-center">
            <div className="inline-flex items-center gap-2 text-accent text-sm font-semibold uppercase tracking-wide mb-4">
              <Puzzle className="w-4 h-4" aria-hidden />
              The honest comparison call
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Talk through your alternatives shortlist
            </h2>
            <p className="text-xl text-white/80 mb-8 leading-relaxed">
              A 30-minute Decision Call with {FOUNDER.name}, founder of EZ Cloud and a 25-year
              Oracle AP specialist with 20+ years specifically in Oracle WebCenter — Content (UCM),
              Imaging (IPM), Capture (ODC), Forms Recognition (WFR), SOA Suite, WebLogic, and ADF.
              Walk through the WebCenter Content footprint, what is driving the replacement
              evaluation, and where EZ Cloud actually fits versus a general ECM alternative.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                size="lg"
                variant="accent"
                className="font-semibold"
                onClick={() => openWebCenterDecisionCall('wcc-alternatives-cta')}
              >
                <Calendar className="mr-2 w-5 h-5" aria-hidden />
                Book an Alternatives Decision Call
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
              <Button size="lg" variant="outline-on-dark" asChild>
                <Link to="/solutions/modernize-oracle-webcenter">
                  See the full modernization path
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default WebCenterContentAlternatives;
